import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { invoke } from '@tauri-apps/api/core';
import { Monitor, LoaderCircle, AlertTriangle, Plug, Unplug, Server } from 'lucide-react';
import { isTauri } from '../utils/tauri';
import FtpConnectionLost from './ftp/FtpConnectionLost';
import { useFtpStore, EMPTY_REMOTE_PANE } from '../store/ftpStore';
import { useConnectionRows } from '../hooks/useConnectionRows';
import { existingNames } from '../utils/transferConflicts';
import { renderFtpConnected } from './ftp/renderFtpConnected';
import { useFtpContextMenus } from './ftp/useFtpContextMenus';
import { useFtpRemoteActions } from './ftp/useFtpRemoteActions';
import { useFtpFolderTransfers } from './ftp/useFtpFolderTransfers';
import { useFtpTransferEngine } from './ftp/useFtpTransferEngine';
import './Ftp.css';

/**
 * FTP / SFTP main panel (chat-style: replaces the whole tab area). A modern
 * FileZilla: LOCAL pane on the left, REMOTE pane on the right, transfers
 * strip at the bottom. All engine work happens Rust-side behind the ftp_*
 * commands (CONTRACTS.md §2); progress arrives on the global
 * `ftp:transfer_progress` Tauri event, routed into ftpStore.upsertTransfer.
 *
 * Transfer paths:
 *  - path-based (double-click, context menu, internal pane-to-pane drag,
 *    the Upload… dialog): ftp_upload / ftp_download with absolute paths.
 *  - OS drops from Finder onto the REMOTE pane: the webview only gets File
 *    blobs (dragDropEnabled is false, so HTML5 events fire but paths don't
 *    exist) → chunked ftp_upload_begin / _chunk (4 MB, base64) / _finish.
 *  - Folders are planned first (utils/folderTransfer.js); an upload onto a
 *    folder of the same name MERGES into it (utils/folderMerge.js).
 *  - Hovering a folder row makes THAT folder the drop target
 *    (utils/ftpRowDrop.js); the transfers strip is ./ftp/FtpTransfers.js.
 */

/* ── Transfers strip ────────────────────────────────────────────────────── */

/* The transfers strip lives in ./ftp/FtpTransfers.js. */

/* ── Main view ──────────────────────────────────────────────────────────── */

export default function FtpView() {
  const { t } = useTranslation();
  const activeConnId = useFtpStore((s) => s.activeConnId);
  const connState = useFtpStore((s) => s.connState);
  const connError = useFtpStore((s) => s.connError);
  // Remote pane is keyed per connId in the store; read the active conn's pane
  // (stable EMPTY_REMOTE_PANE fallback keeps the selector referentially safe).
  const remotePane = useFtpStore((s) => s.remotePanes[s.activeConnId] || EMPTY_REMOTE_PANE);
  const remotePath = remotePane.path;
  const remoteEntries = remotePane.entries;
  /**
   * The ACTIVE connection's remote entries, read from the store at call time.
   *
   * There is no `remoteEntries` field on the store — remote panes are keyed per
   * connection under `remotePanes` — and reading `s.remoteEntries` returned
   * `undefined`. Where that was guarded with `|| []` it did not crash; it made
   * the existing-name check on folder uploads compare against an empty list and
   * silently never refuse. A helper rather than the render-time `remoteEntries`
   * because the callers are async and a folder plan can outlive the render.
   */
  const remoteEntriesNow = () => {
    const st = useFtpStore.getState();
    return (st.remotePanes[st.activeConnId] || EMPTY_REMOTE_PANE).entries || [];
  };

  /**
   * What is ACTUALLY at a destination: name → isDirectory, listed fresh and with
   * hidden files INCLUDED. Every "does this name already exist?" check goes
   * through these two, never through the pane.
   *
   * The panes are filtered by the "show hidden files" preference. A collision
   * check that read them could not see `.htaccess` with that preference off,
   * so "New file → .htaccess" truncated the real one, and downloading a `.ssh`
   * folder into a home directory overwrote the files inside it — the exact
   * failures those checks exist to prevent. Found in review, 13.09.
   *
   * Residual limit, stated rather than hidden: over plain FTP a server that
   * rejects `LIST -a` still omits dotfiles (ftp.rs falls back to `LIST`), so no
   * listing-based check can be airtight there. SFTP always returns them.
   */
  const remoteNamesAt = async (path) =>
    existingNames(
      await invoke('ftp_list_dir', {
        connId: useFtpStore.getState().activeConnId,
        path,
        showHidden: true,
      })
    );
  const localNamesAt = async (path) =>
    existingNames((await invoke('ftp_local_list', { path }))?.entries);
  const remoteLoading = remotePane.loading;
  const remoteError = remotePane.error;
  const remoteSelection = remotePane.selection;
  const localPath = useFtpStore((s) => s.localPath);
  const localEntries = useFtpStore((s) => s.localEntries);
  const localLoading = useFtpStore((s) => s.localLoading);
  const localError = useFtpStore((s) => s.localError);
  const localSelection = useFtpStore((s) => s.localSelection);

  const [nameModal, setNameModal] = useState(null); // { mode: 'mkdir' } | { mode: 'rename', entry }
  const [editFile, setEditFile] = useState(null); // remote file entry open in the File Editor modal
  const [permEntry, setPermEntry] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // { entries: [...] }
  // Shown when the server has neither `zip` nor `python3` to archive with.
  const [zipHelp, setZipHelp] = useState(false);
  // The local file whose remote counterpart is being chosen for Compare.
  const [comparePick, setComparePick] = useState(null);
  // Local entries being exported as an archive.
  const [exportSources, setExportSources] = useState(null);
  const [notice, setNotice] = useState(null);
  const noticeTimerRef = useRef(null);
  // Flipped false on unmount; in-flight chunk loops consult it (plus the store
  // generation) so a logout/unmount aborts them before they touch the store.
  const aliveRef = useRef(true);

  // Personal AND team rows. Reading `ftpStore.connections` here — the
  // personal-only slice — meant a connected TEAM connection matched nothing and
  // the pane rendered "no connection selected" forever, with a live session
  // behind it. The merged rows are display data; the store stays personal-only
  // (see `useConnectionRows`).
  const { rows: connectionRows } = useConnectionRows();
  const activeConn = activeConnId ? connectionRows.find((c) => c.id === activeConnId) : null;
  const activeState = activeConnId ? connState[activeConnId] || 'disconnected' : 'disconnected';
  const protocol = String(activeConn?.index?.protocol || 'sftp').toLowerCase();

  const showNotice = useCallback((msg) => {
    setNotice(msg);
    clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = setTimeout(() => setNotice(null), 4000);
  }, []);
  useEffect(() => () => clearTimeout(noticeTimerRef.current), []);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const {
    startPathUpload, startDownload, uploadBrowserFile, conflictQueue, askConflict, answerConflict,
    transferBatch,
  } = useFtpTransferEngine({ t, aliveRef, showNotice });

  const {
    downloadTargets, pickUploadDialog, downloadFolder, uploadFolder, uploadDroppedFolder,
  } = useFtpFolderTransfers({
    t, remoteNamesAt, localNamesAt, showNotice, startPathUpload, startDownload, uploadBrowserFile,
    askConflict, transferBatch,
  });

  const {
    doArchive, openCompress, moveEntriesTo, makeRowDropHandler, makeDropHandler,
  } = useFtpRemoteActions({
    t, remotePath, activeConnId, remoteNamesAt, setNameModal, setZipHelp, showNotice,
    startPathUpload, startDownload, uploadBrowserFile, transferBatch, downloadFolder, uploadFolder,
    uploadDroppedFolder,
  });

  const {
    buildRemoteMenu, buildRemoteBackgroundMenu, buildLocalBackgroundMenu, buildLocalMenu, doDelete,
  } = useFtpContextMenus({
    t, remotePath, remoteEntries, remoteEntriesNow, remoteSelection, localEntries, localSelection,
    setNameModal, setEditFile, setPermEntry, confirmDelete, setConfirmDelete, setComparePick,
    setExportSources, activeState, protocol, showNotice, startPathUpload, transferBatch,
    downloadTargets, pickUploadDialog, uploadFolder, openCompress,
  });

  /* ── Ghost states ────────────────────────────────────────────────────── */

  if (!isTauri()) {
    return (
      <div className="ftp-view">
        <div className="ftp-empty">
          <div className="ftp-empty-icon">
            <Monitor size={24} />
          </div>
          <span className="ftp-empty-title">{t('ftp.desktopOnly')}</span>
        </div>
      </div>
    );
  }

  // A locked vault gates NEW connections, not the one already on screen: the
  // live session's credentials sit in the Rust session, not the vault, so
  // replacing an active browser with this screen killed nothing but the
  // user's access to their own still-running transfer (lock plan D2 — DB
  // Manager and the Terminal already behave this way).
  //
  // A locked vault no longer TAKES OVER this pane. It used to render a
  // full-page "Vault is locked" screen with its own Unlock button — a whole
  // window given over to one sentence — while the DB Manager, in the same
  // state, simply shows its ordinary empty pane and lets its SIDEBAR carry the
  // locked row (owner, 23.09.2026). The sidebar here says the same thing in the
  // same place, with the same Unlock action, so the screen was repeating it at
  // ten times the size. Falling through lands on the `!activeConn` empty state,
  // which is the truth: nothing is open.
  //
  // This removed the last readers of `vaultLocked`, `unlocking` and
  // `activeSessionLive` in this file. They are gone rather than left behind as
  // subscriptions that re-render the pane for a state it no longer draws.
  if (!activeConn) {
    return (
      <div className="ftp-view">
        <div className="ftp-empty">
          <div className="ftp-empty-icon">
            <Server size={24} />
          </div>
          <span className="ftp-empty-title">{t('ftp.noConnectionTitle')}</span>
          <span className="ftp-empty-sub">{t('ftp.noConnectionSubtitle')}</span>
        </div>
      </div>
    );
  }

  if (activeState === 'connecting') {
    return (
      <div className="ftp-view">
        <div className="ftp-empty">
          <div className="ftp-empty-icon">
            <LoaderCircle size={24} className="animate-spin" />
          </div>
          <span className="ftp-empty-title">{activeConn.name}</span>
          <span className="ftp-empty-sub">{t('ftp.connecting')}</span>
        </div>
      </div>
    );
  }

  if (activeState === 'lost') {
    return <FtpConnectionLost connId={activeConnId} reason={connError[activeConnId]} />;
  }

  if (activeState === 'error') {
    return (
      <div className="ftp-view">
        <div className="ftp-empty">
          <div className="ftp-empty-icon ftp-empty-icon--error">
            <AlertTriangle size={24} />
          </div>
          <span className="ftp-empty-title">{t('ftp.connectionFailed')}</span>
          {connError[activeConnId] && (
            <span className="ftp-empty-sub ftp-empty-sub--error">{connError[activeConnId]}</span>
          )}
          <button
            type="button"
            onClick={() => useFtpStore.getState().connect(activeConnId)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors modern-button text-sm"
            style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Plug size={14} /> {t('ftp.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (activeState !== 'connected') {
    return (
      <div className="ftp-view">
        <div className="ftp-empty">
          <div className="ftp-empty-icon">
            <Unplug size={24} />
          </div>
          <span className="ftp-empty-title">{activeConn.name}</span>
          <span className="ftp-empty-sub">{t('ftp.disconnectedHint')}</span>
          <button
            type="button"
            onClick={() => useFtpStore.getState().connect(activeConnId)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors modern-button text-sm"
            style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Plug size={14} /> {t('ftp.connect')}
          </button>
        </div>
      </div>
    );
  }

  return renderFtpConnected({
    t, remotePath, activeConnId, activeConn, remoteEntries, remoteNamesAt, remoteLoading,
    remoteError, remoteSelection, localPath, localEntries, localLoading, localError, localSelection,
    nameModal, setNameModal, editFile, setEditFile, permEntry, setPermEntry, confirmDelete,
    setConfirmDelete, zipHelp, setZipHelp, comparePick, setComparePick, exportSources,
    setExportSources, notice, showNotice, startPathUpload, startDownload, conflictQueue,
    answerConflict, transferBatch, pickUploadDialog, doArchive, moveEntriesTo, makeRowDropHandler,
    makeDropHandler, buildRemoteMenu, buildRemoteBackgroundMenu, buildLocalBackgroundMenu,
    buildLocalMenu, doDelete,
  });
}
